/* BrandEmptyState — Landing-page brand DNA for empty/first-time surfaces.
 *
 * Use this on any work surface where the user has zero data yet — a
 * conversion moment. It pulls the landing page's design language
 * (gradient mesh, mono uppercase overline, gradient-text emphasis,
 * emerald CTA, glass surface) into the app without making the rest of
 * the work UI feel "marketing-y".
 *
 * Props
 *   overline:  short uppercase label rendered in mono ("GET STARTED", "BANKING")
 *   title:     main headline (renders inside an <h2>)
 *   accent:    optional substring of `title` to highlight with the brand gradient
 *   description: short body paragraph
 *   icon:      Lucide icon component (default: Sparkles)
 *   primaryCta:  { label, onClick, to, testId }   — emerald button
 *   secondaryCta: { label, onClick, to, testId }  — ghost button
 *   tone:        'emerald' (default) | 'violet' | 'blue' | 'amber' — color of mesh + accent
 *   className:   extra container classes
 *   children:    optional extra slot below the CTAs (suggestion grid, tips, etc.)
 *
 * Visual references:
 *   - LandingPageDark hero (gradient mesh, mono overlines, gradient text)
 *   - LoginDark card (glass surface with backdrop-blur)
 *   - AI Hub empty state (brand marks + try-asking grid)
 */
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';


const TONE_MAP = {
  emerald: {
    blob1: 'bg-emerald-500/15',
    blob2: 'bg-violet-500/10',
    blob3: 'bg-blue-500/8',
    accent: 'from-emerald-400 via-violet-400 to-blue-400',
    iconBg: 'from-emerald-400/25 to-violet-500/20 border-white/10',
    iconShadow: 'shadow-emerald-500/20',
    iconColor: 'text-emerald-400',
    ctaShadow: 'shadow-emerald-500/20',
  },
  violet: {
    blob1: 'bg-violet-500/15',
    blob2: 'bg-fuchsia-500/10',
    blob3: 'bg-blue-500/8',
    accent: 'from-violet-400 via-fuchsia-400 to-blue-400',
    iconBg: 'from-violet-400/25 to-fuchsia-500/20 border-white/10',
    iconShadow: 'shadow-violet-500/20',
    iconColor: 'text-violet-400',
    ctaShadow: 'shadow-violet-500/20',
  },
  blue: {
    blob1: 'bg-blue-500/15',
    blob2: 'bg-emerald-500/10',
    blob3: 'bg-violet-500/8',
    accent: 'from-blue-400 via-emerald-400 to-violet-400',
    iconBg: 'from-blue-400/25 to-emerald-500/20 border-white/10',
    iconShadow: 'shadow-blue-500/20',
    iconColor: 'text-blue-400',
    ctaShadow: 'shadow-blue-500/20',
  },
  amber: {
    blob1: 'bg-amber-500/15',
    blob2: 'bg-orange-500/10',
    blob3: 'bg-emerald-500/8',
    accent: 'from-amber-400 via-orange-400 to-emerald-400',
    iconBg: 'from-amber-400/25 to-orange-500/20 border-white/10',
    iconShadow: 'shadow-amber-500/20',
    iconColor: 'text-amber-400',
    ctaShadow: 'shadow-amber-500/20',
  },
};


function highlightAccent(title, accent, gradientClasses) {
  if (!accent || !title.includes(accent)) {
    return <span>{title}</span>;
  }
  const idx = title.indexOf(accent);
  return (
    <>
      <span>{title.slice(0, idx)}</span>
      <span className={`bg-gradient-to-r ${gradientClasses} bg-clip-text text-transparent`}>
        {accent}
      </span>
      <span>{title.slice(idx + accent.length)}</span>
    </>
  );
}


function CtaButton({ cta, variant = 'primary', tone }) {
  if (!cta) return null;
  const isPrimary = variant === 'primary';
  const className = cn(
    'gap-1.5 h-10 px-5',
    isPrimary
      ? cn('bg-emerald-500 hover:bg-emerald-400 text-black font-semibold shadow-lg', tone.ctaShadow)
      : 'bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200',
  );
  const inner = (
    <>
      {cta.label}
      {isPrimary && <ArrowRight className="w-4 h-4" />}
    </>
  );
  if (cta.to) {
    return (
      <Link to={cta.to} data-testid={cta.testId}>
        <Button type="button" size="default" className={className}>{inner}</Button>
      </Link>
    );
  }
  return (
    <Button
      type="button"
      size="default"
      onClick={cta.onClick}
      className={className}
      data-testid={cta.testId}
    >
      {inner}
    </Button>
  );
}


export default function BrandEmptyState({
  overline,
  title,
  accent,
  description,
  icon: Icon = Sparkles,
  primaryCta,
  secondaryCta,
  tone: toneKey = 'emerald',
  className,
  children,
  testId = 'brand-empty-state',
}) {
  const tone = TONE_MAP[toneKey] || TONE_MAP.emerald;
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border border-white/10 bg-black/85 backdrop-blur-xl text-zinc-100',
        'px-6 sm:px-10 py-12 sm:py-16',
        className,
      )}
      data-testid={testId}
    >
      {/* Mesh blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className={cn('absolute top-[-30%] left-[-15%] w-[420px] h-[420px] rounded-full blur-[100px]', tone.blob1)} />
        <div className={cn('absolute top-[10%] right-[-20%] w-[500px] h-[500px] rounded-full blur-[120px]', tone.blob2)} />
        <div className={cn('absolute bottom-[-40%] left-[20%] w-[600px] h-[600px] rounded-full blur-[140px]', tone.blob3)} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.04)_1px,transparent_0)] [background-size:32px_32px]" />
      </div>

      <div className="relative z-10 text-center max-w-2xl mx-auto">
        {overline && (
          <p className="text-[11px] font-mono uppercase tracking-[0.28em] text-zinc-500 mb-5">
            {overline}
          </p>
        )}

        <div
          className={cn(
            'inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br border shadow-2xl mb-5',
            tone.iconBg,
            tone.iconShadow,
          )}
        >
          <Icon className={cn('w-8 h-8', tone.iconColor)} />
        </div>

        <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white leading-[1.05] mb-4">
          {highlightAccent(title, accent, tone.accent)}
        </h2>

        {description && (
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-md mx-auto mb-8">
            {description}
          </p>
        )}

        {(primaryCta || secondaryCta) && (
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <CtaButton cta={primaryCta} variant="primary" tone={tone} />
            <CtaButton cta={secondaryCta} variant="secondary" tone={tone} />
          </div>
        )}

        {children && <div className="mt-10">{children}</div>}
      </div>
    </div>
  );
}
